import React from 'react';
import PropTypes from 'prop-types';
import AccountShowField from 'components/show/AccountShowField';
import Actions from 'components/ui/general/Actions';
import BookingActivities from '../bookingActivities/BookingActivities';
import BookingAmendContainer from '../amend/BookingAmendContainer';
import BookingReceipt from './BookingReceipt';
import BookingRefund from 'pages/bookings/show/actions/refund/BookingRefund';
import BookingReservations from '../bookingReservations/BookingReservations';
import Breadcrumbs from 'components/layout/Layout/Breadcrumbs';
import CancelBooking from './actions/CancelBooking';
import Column from '../../../components/ui/show/Column';
import ConsumerCommunicationNotification from 'components/notifications/ConsumerCommunicationNotification';
import DateTimeUTCTextField from 'components/show/DateFields/DateTimeUTCTextField';
import FullNameTextField from 'components/show/FullNameTextField';
import Header from 'components/ui/general/Header';
import InnerColumn from 'components/ui/show/InnerColumn';
import MultiBookingInformationNotification from 'components/notifications/MultiBookingInformationNotification';
import Notification from 'components/ui/general/Notification';
import PaymentSlipActions from 'components/button/payment-slip/PaymentSlipActions';
import ReferenceShowField from 'components/show/ReferenceShowField';
import RefreshButton from 'components/button/RefreshButton';
import RetryButton from 'components/button/RetryButton';
import Section from 'components/ui/general/Section';
import SendMailModal from 'components/modals/SendMailModal';
import Status from './BookingStatus';
import TestBookingNotification from 'components/notifications/TestBookingNotification';
import TextField from 'components/show/TextField';
import Title from 'components/ui/general/Title';
import VoucherActions from 'components/button/Voucher/VoucherActions';
import authHelper from 'helpers/auth';
import languages from 'helpers/languages';
import utils from 'helpers/utils';
import { fetchCampaignAccumulated } from 'store/actions/campaigns/fetchCampaigns';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import UpdateVoucherFormModal from 'components/button/Voucher/UpdateVoucherFormModal';
import EntityShowActions from '../../../components/button/actions/EntityShowActions';
import DownloadPDF from 'components/button/DownloadPDF';
import { downloadBookingInvoiceRequest } from 'http/requests/bookings/bookingDownloadRequests';

const PaidOnInvoiceMessage = styled.div`
    position: absolute;
    bottom: 10px;
    font-size: 14px;
    font-style: italic;
    line-height: 1.5;
`;

const BookingShow = (props) => {
    const {
        record,
        refreshActivities,
        refund,
        showVoucherModal,
        toggleVoucherModal,
        translate,
    } = props;

    const {
        consumer_communication,
        id,
        locale,
        payment_method,
    } = record;

    const isAdmin = authHelper.isAdmin();
    const isProvider = authHelper.isProvider();

    const isPaidOnInvoice = payment_method === 'deferred';

    const isBookingEditable = record.policies.update.allowed;
    const bookingCanBeCancelled = record.policies.cancel.allowed;
    const bookingCanBeAmended = record.policies.amend.allowed;
    const bookingCanBeRefunded = record.policies.refund.allowed;

    const bookingIsCancelled = record.reservation_status === 'canceled';
    const bookingIsCompleted = record.reservation_status === 'completed';
    const bookingIsUnderReview = record.reservation_status === 'under_review';

    const fullName = utils.fullName(record.first_name, record.last_name);
    const userInfo = `${ fullName } (${ record.order_id })`;

    const breadcrumbs = [
        {
            label: translate('resources.bookings.title'),
            link: '/bookings',
        },
        {
            label: userInfo,
        },
    ];

    return (
        <div>
            <Breadcrumbs breadcrumbs={ breadcrumbs } />

            <Header>
                <Title title={ `${ utils.fullName(record.first_name, record.last_name) } (${ record.order_id })` } />

                <Actions>
                    { bookingCanBeAmended &&
                        <BookingAmendContainer id={ record.id } />
                    }
                    { isAdmin && bookingIsUnderReview &&
                        <RetryButton id={ id } />
                    }

                    { bookingCanBeRefunded &&
                        <BookingRefund
                            booking={ record }
                            refund={ refund }
                        />
                    }

                    { bookingCanBeCancelled &&
                        <CancelBooking
                            id={ id }
                            orderId={ record.order_id }
                        />
                    }

                    { (!isProvider && bookingIsCompleted) &&
                        <VoucherActions
                            id={ id }
                            orderId={ record.order_id }
                            toggleVoucherModal={ toggleVoucherModal }
                        />
                    }

                    { (!isProvider && bookingIsCompleted) &&
                        <PaymentSlipActions
                            id={ id }
                            orderId={ record.order_id }
                        />
                    }

                    { isAdmin &&
                        <EntityShowActions
                            actions={ [
                                <DownloadPDF
                                    key='booking-invoice-download'
                                    fileName={ `booking_${ record.order_id }_invoice.pdf` }
                                    request={ downloadBookingInvoiceRequest }
                                    title={ translate('resources.invoices.download') }
                                />,
                            ] }
                            id={ id }
                            title={ translate('filters.components.invoice.label') }
                        />
                    }
                </Actions>
            </Header>

            <Notification>
                <MultiBookingInformationNotification data={ record } />

                <ConsumerCommunicationNotification data={ record } />

                <TestBookingNotification data={ record } />
            </Notification>

            <Section>
                <Column
                    editEnabled={ isBookingEditable && !bookingIsCancelled }
                    editRoute={ `/orders/${ id }` }
                    size={ 8 }
                    title={ translate('resources.orders.show.reservation_details') }
                >
                    <InnerColumn>
                        <FullNameTextField
                            data={ record }
                            label='resources.orders.fields.full_name'
                        />

                        { (!isProvider) &&
                            <TextField
                                label='resources.orders.fields.email'
                                value={ record.email }
                            />
                        }

                        <TextField
                            label='resources.orders.fields.customer_remarks'
                            value={ record.customer_remarks }
                        />

                        { locale &&
                            <TextField
                                label='resources.orders.fields.locale'
                                value={ translate(`languages.${ languages.getLabel(locale).toLowerCase() }`) }
                            />
                        }
                    </InnerColumn>

                    <InnerColumn>
                        <TextField
                            label='resources.orders.fields.license_plate'
                            value={ record.license_plate }
                        />

                        <TextField
                            label='resources.orders.fields.phone'
                            value={ record.phone }
                        />

                        <TextField
                            label='resources.orders.fields.return_flight_number'
                            value={ record.return_flight_number }
                        />
                    </InnerColumn>
                </Column>

                <Column
                    actions={ <Status record={ record } /> }
                    size={ 4 }
                    title={ translate('resources.orders.show.reservation') }
                >
                    <InnerColumn>
                        <BookingReceipt record={ record } />

                        { isPaidOnInvoice && <PaidOnInvoiceMessage>
                            { translate('resources.orders.show.paid_on_invoice') }
                        </PaidOnInvoiceMessage> }
                    </InnerColumn>
                </Column>
            </Section>

            <BookingReservations
                id={ id }
            />

            <Section>
                <Column
                    size={ isAdmin ? 8 : 12 }
                    title={ translate('resources.orders.show.booking_information') }
                >
                    <InnerColumn>
                        <DateTimeUTCTextField
                            label='resources.orders.fields.created_at'
                            value={ record.created_at }
                        />
                    </InnerColumn>

                    <InnerColumn>
                        <DateTimeUTCTextField
                            label='resources.orders.fields.updated_at'
                            value={ record.updated_at }
                        />
                    </InnerColumn>

                    <InnerColumn>
                        { (record.funnel_id && !isProvider) &&
                            <ReferenceShowField
                                action={ fetchCampaignAccumulated }
                                display='name'
                                label='resources.campaigns.name'
                                resource='campaigns'
                                value={ record.funnel_id }
                            />
                        }
                    </InnerColumn>
                </Column>

                {
                    isAdmin &&
                        <Column
                            size={ 4 }
                            title={ translate('resources.orders.show.reseller') }
                        >
                            <InnerColumn>
                                <AccountShowField
                                    hideLabel
                                    value={ record.reseller_id }
                                />
                            </InnerColumn>
                        </Column>
                }
            </Section>

            { isAdmin &&
                <Section>
                    <Column
                        actions={ <RefreshButton id={ id } onClick={ refreshActivities } /> }
                        size={ 12 }
                        title={ translate('resources.orders.show.activities.booking.label') }
                    >
                        <InnerColumn>
                            <BookingActivities record={ record } />
                        </InnerColumn>
                    </Column>
                </Section>
            }

            { consumer_communication &&
                <SendMailModal id={ id } />
            }

            {
                showVoucherModal &&
                <UpdateVoucherFormModal
                    id={ id }
                    toggleVoucherModal={ toggleVoucherModal }
                />
            }
        </div>
    );
};

BookingShow.propTypes = {
    record: PropTypes.shape({
        consumer_communication: PropTypes.bool,
        created_at: PropTypes.string,
        customer_remarks: PropTypes.string,
        destination_address: PropTypes.string,
        destination_arrival_date: PropTypes.string,
        destination_departure_date: PropTypes.string,
        email: PropTypes.string,
        first_name: PropTypes.string,
        funnel_id: PropTypes.number,
        id: PropTypes.number,
        last_name: PropTypes.string,
        license_plate: PropTypes.string,
        locale: PropTypes.string,
        number_of_persons: PropTypes.number,
        order_id: PropTypes.number,
        origin_address: PropTypes.string,
        payment_method: PropTypes.string,
        payment_status: PropTypes.string,
        policies: PropTypes.shape({
            cancel: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }),
            amend: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }),
            refund: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }),
            update: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }),
        }),
        phone: PropTypes.string,
        product_info: PropTypes.shape({
            category_id: PropTypes.number,
        }),
        reseller_id: PropTypes.number,
        reservation_status: PropTypes.string,
        return_flight_number: PropTypes.string,
        updated_at: PropTypes.string,
    }).isRequired,
    refund: PropTypes.func.isRequired,
    refreshActivities: PropTypes.func.isRequired,
    showVoucherModal: PropTypes.bool.isRequired,
    toggleVoucherModal: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default translate(BookingShow);



// WEBPACK FOOTER //
// ./src/pages/bookings/show/BookingShow.js